import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { AdminOrder, AdminUserRecord, TopMetric, LowStockItem, PaymentQueueItem, AdminProductRecord } from '@core/models/admin.model';
import { ApiUser } from '@core/models/api/user.model';
import { CreateOrderRequest } from '@core/models/api/order.model';
import { TokenService } from '@core/services/token.service';
import { AuthApiService } from '@core/services/api/auth-api.service';
import { UsersApiService } from '@core/services/api/users-api.service';
import { ProductsApiService } from '@core/services/api/products-api.service';
import { OrdersApiService } from '@core/services/api/orders-api.service';
import { PaymentsApiService } from '@core/services/api/payments-api.service';
import { DashboardApiService } from '@core/services/api/dashboard-api.service';
import {
  toAdminOrder,
  toAdminProductRecord,
  toAdminUserRecord,
  toApiOrderStatus,
  toApiPaymentMethod,
  toCreateUserRequest,
  toFrontendRole,
  toLowStockItem,
  toPaymentQueueItem,
  toProductFormValues,
  toTopMetrics,
  toUpdateUserRequest
} from '@core/mappers/admin.mappers';

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private router = inject(Router);
  private tokenService = inject(TokenService);
  private authApi = inject(AuthApiService);
  private usersApi = inject(UsersApiService);
  private productsApi = inject(ProductsApiService);
  private ordersApi = inject(OrdersApiService);
  private paymentsApi = inject(PaymentsApiService);
  private dashboardApi = inject(DashboardApiService);

  isLoggedIn: boolean = false;
  currentUsername: string = '';
  currentEmail: string = '';
  /** Real backend user UUID of the logged-in account, once a session is established. */
  currentUserId: string | null = null;
  currentUserRole: 'Admin' | 'Manager' | 'Customer' = 'Customer';
  /** Full profile of the logged-in user, sourced from the login/`/users/me` response — used by the customer dashboard, which cannot list all users. */
  currentUserProfile: AdminUserRecord | null = null;
  activeTab: 'dashboard' | 'users' | 'products' | 'orders' = 'dashboard';

  // These start empty and are populated from the real API — see loadUsers()/loadProducts()/
  // loadOrders()/loadPayments()/loadDashboardSummary()/loadLowStockWatchlist().
  recentUsers: AdminUserRecord[] = [];
  recentProducts: AdminProductRecord[] = [];
  recentOrders: AdminOrder[] = [];
  paymentQueue: PaymentQueueItem[] = [];
  topMetrics: TopMetric[] = [];
  lowStockWatchlist: LowStockItem[] = [];

  getOrderedQtyForProduct(productName: string): number {
    if (!this.recentOrders || this.recentOrders.length === 0 || !productName) return 0;
    const lowerName = productName.trim().toLowerCase();
    return this.recentOrders.reduce((sum, order) => {
      if (!order.items) return sum;
      const itemSum = order.items.reduce((iSum, item) => {
        if (item.name && item.name.trim().toLowerCase() === lowerName) {
          return iSum + (Number(item.qty) || 0);
        }
        return iSum;
      }, 0);
      return sum + itemSum;
    }, 0);
  }

  getRemainingStockQty(product: AdminProductRecord): number {
    if (!product) return 0;
    let initialQty = 0;
    if (product.qty !== undefined && product.qty !== null && product.qty !== '') {
      initialQty = Number(product.qty);
    } else if (product.stock) {
      const match = product.stock.trim().match(/^([\d.]+)/);
      if (match) initialQty = Number(match[1]);
    }
    const ordered = this.getOrderedQtyForProduct(product.name);
    return Math.max(0, initialQty - ordered);
  }

  getRemainingStockString(product: AdminProductRecord): string {
    if (!product) return '0 Kg';
    const rem = this.getRemainingStockQty(product);
    const unit = product.unit || (product.stock ? product.stock.replace(/^[\d.\s]+/, '') : '') || 'Kg';
    return `${rem} ${unit}`;
  }

  constructor() {
    if (this.tokenService.hasAccessToken()) {
      this.hydrateSession();
    }
  }

  private hydrateSession(): void {
    this.usersApi.getMe().subscribe({
      next: apiUser => this.applySession(apiUser),
      error: () => {
        this.tokenService.clear();
        this.isLoggedIn = false;
      }
    });
  }

  private applySession(apiUser: ApiUser): void {
    this.isLoggedIn = true;
    this.currentUsername = apiUser.name;
    this.currentEmail = apiUser.email;
    this.currentUserId = apiUser.id;
    this.currentUserRole = toFrontendRole(apiUser.role);
    this.currentUserProfile = toAdminUserRecord(apiUser);
  }

  private loadInitialDataForRole(): void {
    this.loadOrders();
    this.loadProducts();
    this.loadPayments();
    if (this.currentUserRole !== 'Customer') {
      this.loadUsers();
      this.loadDashboardSummary();
      this.loadLowStockWatchlist();
    }
  }

  /** Real login against POST /auth/login — replaces the old username-guessing mock. */
  login(email: string, password: string): Observable<AdminUserRecord> {
    return this.authApi.login({ email, password }).pipe(
      tap(response => {
        this.tokenService.setTokens(response.accessToken, response.refreshToken);
        this.applySession(response.user);
        this.loadInitialDataForRole();
        if (this.currentUserRole === 'Customer') {
          this.router.navigate(['/customer', 'overview']);
        } else {
          this.router.navigate(['/admin', this.activeTab || 'dashboard']);
        }
      }),
      map(response => toAdminUserRecord(response.user)),
      catchError((error: unknown) => throwError(() => error))
    );
  }

  logoutUser(): void {
    this.tokenService.clear();
    this.isLoggedIn = false;
    this.currentUserId = null;
    this.currentUserProfile = null;
    this.recentUsers = [];
    this.recentProducts = [];
    this.recentOrders = [];
    this.paymentQueue = [];
    this.router.navigate(['/']);
  }

  setActiveTab(tab: 'dashboard' | 'users' | 'products' | 'orders'): void {
    this.activeTab = tab;
    if (this.isLoggedIn) {
      this.router.navigate(['/admin', tab]);
    }
  }

  refreshData(): void {
    switch (this.activeTab) {
      case 'users':
        this.loadUsers();
        break;
      case 'products':
        this.loadProducts();
        break;
      case 'orders':
        this.loadOrders();
        break;
      default:
        this.loadInitialDataForRole();
    }
  }

  // ---------------------------------------------------------------------
  // Payments
  // ---------------------------------------------------------------------

  loadPayments(): void {
    this.paymentsApi.getAll().subscribe({
      next: apiPayments => {
        this.paymentQueue = apiPayments.map(toPaymentQueueItem);
      },
      error: error => console.error('Failed to load payments', error)
    });
  }

  /** Records a settlement against an order. Always comes back PENDING -- see PaymentsService.create on the backend. */
  createPayment(orderApiId: string, amount: number, paymentMethod: string): Observable<PaymentQueueItem> {
    return this.paymentsApi
      .create({ orderId: orderApiId, amount, paymentMethod: toApiPaymentMethod(paymentMethod) })
      .pipe(
        map(toPaymentQueueItem),
        tap(created => {
          this.paymentQueue = [created, ...this.paymentQueue];
        })
      );
  }

  togglePaymentStatus(item: PaymentQueueItem): Observable<PaymentQueueItem> {
    if (!item.apiId) {
      return throwError(() => new Error('This payment is missing its backend id and cannot be updated.'));
    }
    return this.paymentsApi.toggleStatus(item.apiId).pipe(
      map(toPaymentQueueItem),
      tap(updated => {
        const index = this.paymentQueue.findIndex(p => p.apiId === updated.apiId);
        if (index !== -1) {
          this.paymentQueue[index] = updated;
        }
        if (updated.status === 'VERIFIED') {
          const matchingOrder = this.recentOrders.find(o => o.id === updated.orderId);
          if (matchingOrder) {
            matchingOrder.secondaryStatus = 'PAID';
          }
        }
      })
    );
  }

  // ---------------------------------------------------------------------
  // Users
  // ---------------------------------------------------------------------

  loadUsers(): void {
    this.usersApi.getAll().subscribe({
      next: apiUsers => {
        this.recentUsers = apiUsers.map(toAdminUserRecord);
      },
      error: error => console.error('Failed to load users', error)
    });
  }

  addUser(newUser: Partial<AdminUserRecord>): Observable<AdminUserRecord> {
    return this.usersApi.create(toCreateUserRequest(newUser)).pipe(
      map(toAdminUserRecord),
      tap(created => {
        this.recentUsers = [created, ...this.recentUsers];
      })
    );
  }

  updateUser(updatedUser: AdminUserRecord): Observable<AdminUserRecord> {
    const targetId = updatedUser.apiId ?? updatedUser.id;
    return this.usersApi.update(targetId, toUpdateUserRequest(updatedUser)).pipe(
      map(toAdminUserRecord),
      tap(saved => {
        const index = this.recentUsers.findIndex(u => (u.apiId ?? u.id) === targetId);
        if (index !== -1) {
          this.recentUsers[index] = saved;
        } else {
          this.recentUsers = [saved, ...this.recentUsers];
        }
      })
    );
  }

  deleteUser(user: AdminUserRecord): Observable<void> {
    const targetId = user.apiId ?? user.id;
    return this.usersApi.delete(targetId).pipe(
      tap(() => {
        this.recentUsers = this.recentUsers.filter(u => (u.apiId ?? u.id) !== targetId);
      })
    );
  }

  // ---------------------------------------------------------------------
  // Products
  // ---------------------------------------------------------------------

  loadProducts(): void {
    this.productsApi.getAll().subscribe({
      next: apiProducts => {
        this.recentProducts = apiProducts.map(toAdminProductRecord);
      },
      error: error => console.error('Failed to load products', error)
    });
  }

  addProduct(newProduct: Partial<AdminProductRecord>, imageFile?: File[] | File | null): Observable<AdminProductRecord> {
    return this.productsApi.create(toProductFormValues(newProduct), imageFile).pipe(
      map(toAdminProductRecord),
      tap(created => {
        this.recentProducts = [created, ...this.recentProducts];
      })
    );
  }

  updateProduct(updatedProduct: AdminProductRecord, imageFile?: File[] | File | null): Observable<AdminProductRecord> {
    const targetId = updatedProduct.apiId ?? updatedProduct.sku;
    return this.productsApi.update(targetId, toProductFormValues(updatedProduct), imageFile).pipe(
      map(toAdminProductRecord),
      tap(saved => {
        const index = this.recentProducts.findIndex(p => (p.apiId ?? p.sku) === targetId);
        if (index !== -1) {
          this.recentProducts[index] = saved;
        } else {
          this.recentProducts = [saved, ...this.recentProducts];
        }
      })
    );
  }

  deleteProduct(product: AdminProductRecord): Observable<void> {
    const targetId = product.apiId ?? product.sku;
    return this.productsApi.delete(targetId).pipe(
      tap(() => {
        this.recentProducts = this.recentProducts.filter(p => (p.apiId ?? p.sku) !== targetId);
      })
    );
  }

  deleteProductImage(product: AdminProductRecord, imageUrl?: string, imageIndex?: number): Observable<AdminProductRecord> {
    const targetId = product.apiId ?? product.sku;
    return this.productsApi.deleteImage(targetId, imageUrl, imageIndex).pipe(
      map(toAdminProductRecord),
      tap(saved => {
        const index = this.recentProducts.findIndex(p => (p.apiId ?? p.sku) === targetId);
        if (index !== -1) {
          this.recentProducts[index] = saved;
        }
      })
    );
  }

  // ---------------------------------------------------------------------
  // Orders
  // ---------------------------------------------------------------------

  loadOrders(): void {
    this.ordersApi.getAll().subscribe({
      next: apiOrders => {
        this.recentOrders = apiOrders.map(toAdminOrder);
      },
      error: error => console.error('Failed to load orders', error)
    });
  }

  createOrder(payload: CreateOrderRequest): Observable<AdminOrder> {
    return this.ordersApi.create(payload).pipe(
      map(toAdminOrder),
      tap(created => {
        this.recentOrders = [created, ...this.recentOrders];
      })
    );
  }

  updateOrderStatus(apiIdOrOrder: any, status: string, secondaryStatus?: string): Observable<AdminOrder> {
    const apiId = typeof apiIdOrOrder === 'string' ? apiIdOrOrder : apiIdOrOrder?.apiId;
    if (!apiId || !status) {
      return throwError(() => new Error('Invalid order ID or status'));
    }
    const apiStatus = toApiOrderStatus(status);
    return this.ordersApi.updateStatus(apiId, { status: apiStatus, secondaryStatus }).pipe(
      map(toAdminOrder),
      tap(() => this.loadOrders())
    );
  }

  deleteOrder(apiId?: string): Observable<void> {
    if (!apiId) {
      return throwError(() => new Error('Invalid order ID'));
    }
    return this.ordersApi.delete(apiId).pipe(
      tap(() => this.loadOrders())
    );
  }

  // ---------------------------------------------------------------------
  // Dashboard (admin/manager only -- GET /dashboard/summary is role-restricted)
  // ---------------------------------------------------------------------

  loadDashboardSummary(): void {
    this.dashboardApi.getSummary().subscribe({
      next: summary => {
        this.topMetrics = toTopMetrics(summary);
      },
      error: error => console.error('Failed to load dashboard summary', error)
    });
  }

  loadLowStockWatchlist(): void {
    this.productsApi.getAll({ lowStock: true }).subscribe({
      next: apiProducts => {
        this.lowStockWatchlist = apiProducts.map(toLowStockItem);
      },
      error: error => console.error('Failed to load low stock watchlist', error)
    });
  }
}
